import {
  Box,
  Button,
  Header,
  Pagination,
  SpaceBetween,
  StatusIndicator,
  Table,
  TableProps,
} from '@cloudscape-design/components';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import type { components } from '../api/schema';
import { getPlaceZoomInfoCompany, getPlaceZoomInfoContacts } from '../common/api/places';
import { exportExcel } from '../common/excel';
import { formatDateString } from '../common/formatting/dateFormat';
import { formatName } from '../common/names';
import { Email } from './Email';
import { ResourceError } from './Loading';
import { PhoneNumber } from './PhoneNumber';

type ZoomInfoContactRead = components['schemas']['ZoomInfoContactRead'];

export type ZoomInfoContactTableProps = {
  uuid: string;
  now: Date;
  setNow: (now: Date) => void;
  sortingField?: string;
  setSortingField?: (field: string | undefined) => void;
  sortDirection?: 'asc' | 'desc';
  setSortDirection?: (direction: 'asc' | 'desc') => void;
  hideActions?: boolean;
  variant?: TableProps.Variant;
};

const PAGE_SIZE = 25;

export function ZoomInfoContactTable(props: ZoomInfoContactTableProps) {
  const {
    uuid,
    now,
    setNow,
    sortingField,
    setSortingField,
    sortDirection,
    setSortDirection,
  } = props;
  const resourceName = 'ZoomInfo Contacts';
  const endpointName = 'zoominfo-contacts';

  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(1);

  const companyQuery = useQuery({
    queryKey: ['zoominfo-company', uuid],
    queryFn: () => getPlaceZoomInfoCompany(uuid),
    staleTime: 5 * 60_000,
  });

  const { isPending, isFetching, error, data, refetch } = useQuery({
    queryKey: [endpointName, uuid, sortingField, sortDirection, page, now],
    queryFn: () =>
      getPlaceZoomInfoContacts(uuid, page, PAGE_SIZE, sortingField, sortDirection, now),
    staleTime: 60_000,
  });

  const loading = (isPending || isFetching) && !error;

  useEffect(() => {
    if (data && data.pages !== pageCount) {
      setPageCount(data.pages);
    }
  }, [data, pageCount]);

  const refresh = () => {
    setNow(new Date());
    refetch();
    companyQuery.refetch();
  };

  const pagination = (
    <Pagination
      currentPageIndex={page}
      disabled={loading}
      pagesCount={pageCount || 1}
      onChange={({ detail }) => setPage(detail.currentPageIndex)}
    />
  );

  const company = companyQuery.data ?? null;
  const description = company
    ? `Matched ZoomInfo company: ${company.name ?? 'Unknown'} (id ${company.zoominfo_company_id})`
    : companyQuery.isFetching
      ? 'Looking up matched ZoomInfo company...'
      : 'No ZoomInfo company match cached for this place yet.';

  return (
    <Table<ZoomInfoContactRead>
      onSortingChange={({ detail }) => {
        setSortingField?.(detail.sortingColumn.sortingField);
        setSortDirection?.(detail.isDescending ? 'desc' : 'asc');
      }}
      sortingColumn={{ sortingField }}
      sortingDescending={sortDirection === 'desc'}
      header={
        <Header
          variant={props.variant === 'full-page' || !props.variant ? 'awsui-h1-sticky' : 'h2'}
          description={description}
          actions={
            props.hideActions ? undefined : (
              <SpaceBetween size="xs" direction="horizontal">
                <Button variant="normal" iconName="refresh" onClick={refresh} disabled={loading} />
                <Button
                  disabled={loading || !data?.items?.length}
                  iconName="download"
                  onClick={() => {
                    exportExcel(
                      (data?.items ?? []).map((item) => ({
                        first_name: item.first_name,
                        last_name: item.last_name,
                        job_title: item.job_title,
                        management_level: item.management_level,
                        email: item.email,
                        phone: item.phone,
                        mobile_phone: item.mobile_phone,
                        contact_accuracy_score: item.contact_accuracy_score,
                        city: item.city,
                        state: item.state,
                        country: item.country,
                        last_enriched_at: item.last_enriched_at,
                      })),
                      'zoominfo-contacts',
                    );
                  }}
                />
              </SpaceBetween>
            )
          }
          counter={data?.total ? `(${data.total.toLocaleString()})` : undefined}
        >
          {resourceName}
        </Header>
      }
      items={data?.items ?? []}
      loading={loading}
      loadingText={`Loading ${resourceName}...`}
      pagination={pagination}
      variant={props.variant ?? 'full-page'}
      stickyHeader={!props.variant || props.variant === 'full-page'}
      empty={
        <Box color="inherit" margin={{ vertical: 'xs' }} textAlign="center">
          {error ? (
            <ResourceError error={error} refetch={refresh} resourceName={resourceName} />
          ) : (
            <SpaceBetween size="xxs">
              <div>
                <b>No {resourceName.toLowerCase()}</b>
                <Box color="inherit" variant="p">
                  No ZoomInfo contacts enriched for this place yet.
                </Box>
              </div>
            </SpaceBetween>
          )}
        </Box>
      }
      columnDefinitions={[
        {
          id: 'name',
          header: 'Name',
          cell: (item: ZoomInfoContactRead) => (
            <SpaceBetween direction="vertical" size="xxxs">
              <Box>{formatName(item.first_name ?? null, item.last_name ?? null)}</Box>
              <Box>{item.job_title ?? '-'}</Box>
              {item.management_level && (
                <Box color="text-body-secondary">{item.management_level}</Box>
              )}
            </SpaceBetween>
          ),
          sortingField: 'last_name',
        },
        {
          id: 'contact_info',
          header: 'Contact Info',
          cell: (item: ZoomInfoContactRead) => (
            <SpaceBetween direction="vertical" size="xxxs">
              <Email email={item.email ?? null} />
              {item.phone && (
                <PhoneNumber
                  phoneNumber={item.phone}
                  lineTypeInfo={item.phone_line_type_info}
                  showLineTypeInfo
                />
              )}
              {item.mobile_phone && (
                <PhoneNumber
                  phoneNumber={item.mobile_phone}
                  lineTypeInfo={item.mobile_phone_line_type_info}
                  showLineTypeInfo
                />
              )}
            </SpaceBetween>
          ),
        },
        {
          id: 'location',
          header: 'Location',
          cell: (item: ZoomInfoContactRead) => {
            const parts = [item.city, item.state, item.zip_code].filter(Boolean);
            return parts.length ? parts.join(', ') : '-';
          },
        },
        {
          id: 'contact_accuracy_score',
          header: 'Score',
          cell: (item: ZoomInfoContactRead) =>
            item.contact_accuracy_score != null ? (
              <StatusIndicator
                type={
                  item.contact_accuracy_score >= 95
                    ? 'success'
                    : item.contact_accuracy_score >= 85
                      ? 'info'
                      : 'warning'
                }
              >
                {item.contact_accuracy_score.toFixed(0)}
              </StatusIndicator>
            ) : (
              '-'
            ),
          sortingField: 'contact_accuracy_score',
        },
        {
          id: 'last_enriched_at',
          header: 'Enriched',
          cell: (item: ZoomInfoContactRead) =>
            item.last_enriched_at ? formatDateString(item.last_enriched_at) : '-',
          sortingField: 'last_enriched_at',
        },
      ]}
    />
  );
}
